import React, {
  useState,
  useMemo,
  ReactNode,
  useEffect,
  useCallback,
  useId,
  forwardRef,
  useImperativeHandle,
} from 'react';
import classes from 'classnames';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import styles from './GenericTable.module.scss';
import Pagination from '@/components/school-management/Pagination/Pagination';
import { Link } from 'react-router-dom';
import AppIcon from '@/components/school-management/AppIcon/AppIcon';
import * as XLSX from 'xlsx';
import SkeltonLoader from '@/components/school-management/SkeltonLoader/SkeltonLoader';

export interface TableColumn<T> {
  key: string;
  label: string;
  width?: string;
  sortable?: boolean;
  render?: (value: any, row: T) => ReactNode;
  xlsxRender?: (value: any, row: T) => number | string | Date;
  xlsxLabel?: string;
  hidden?: boolean;
  filterOptions?: string[];
  filterValue?: string;
  onFilterChange?: (value: string) => void;
}

export interface SelectedAction {
  text: string | ((count: number) => string);
  key: string;
  hidden?: boolean;
}

export interface GenericTableProps<T> {
  title?: string;
  subTitle?: string;
  levelHint?: string;
  isShowLevelHint?: boolean;
  onDownload?: () => void;
  downloadFileName?: string;
  columns: TableColumn<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  rowKey: (row: T) => string | number;
  detailsPath?: (row: T) => string;
  pageSize?: number;
  noResultsMessage?: string;
  className?: string;
  disableScroll?: boolean;
  disablePagination?: boolean;
  variant?: 'compact' | 'card';
  emptyStateAction?: React.ReactNode;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  emptyStateSubDescription?: string;
  emptyStateWidth?: number;
  hideHeaders?: boolean;
  hasData?: boolean;
  showSortArrows?: boolean;
  style?: React.CSSProperties;
  // Server-side sorting props
  serverSideSorting?: boolean;
  sortBy?: string | null;
  sortDirection?: 'asc' | 'desc' | null;
  onSortChange?: (sortBy: string | null, sortDirection: 'asc' | 'desc' | null) => void;
  showLoader?: boolean;
  onTableViewDataChange?: (tableViewData: T[]) => void;
  serverSidePagination?: boolean;
  currentPage?: number;
  totalPages?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  serverSideDownload?: boolean;
  isMultiSelect?: boolean;
  maxSelectItems?: number;
  titleRenderOnSelected?: (num: number) => string;
  selectedActions?: SelectedAction[];
  onAction?: (key: string, items: T[]) => void;
  onItemsSelect?: (items: T[]) => void;
  showSearch?: boolean;
  searchPlaceholder?: string;
  maxSearchQuery?: number;
  searchDescription?: string;
  onSearchQueryChange?: (searchQuery: string) => void;
  showHeaderInEmptyState?: boolean;
}

export interface GenericTableHandle<T> {
  getSelectedItems: () => T[];
  setSelectedItems: (items: T[]) => void;
  getSearchQuery: () => string;
  setSearchQuery: (searchQuery: string) => void;
}

function GenericTable<T>(
  {
    title,
    subTitle,
    onDownload,
    levelHint,
    isShowLevelHint,
    downloadFileName,
    columns,
    data,
    onRowClick,
    rowKey,
    detailsPath,
    pageSize = 50,
    noResultsMessage,
    className,
    disableScroll = false,
    disablePagination = false,
    variant = 'card',
    emptyStateAction,
    emptyStateTitle,
    emptyStateDescription,
    emptyStateSubDescription,
    emptyStateWidth,
    hideHeaders,
    hasData,
    showSortArrows = true,
    style,
    serverSideSorting = false,
    sortBy: externalSortBy,
    sortDirection: externalSortDirection,
    onSortChange,
    showLoader,
    onTableViewDataChange,
    serverSidePagination = false,
    currentPage = 1,
    totalPages = 1,
    totalCount = 0,
    onPageChange,
    serverSideDownload = false,
    isMultiSelect = false,
    maxSelectItems = 0,
    titleRenderOnSelected = (num: number) => `${num} Items Selected`,
    selectedActions = [],
    onAction,
    onItemsSelect,
    showSearch = false,
    searchPlaceholder = 'Search',
    maxSearchQuery = 100,
    searchDescription,
    onSearchQueryChange,
    showHeaderInEmptyState = false,
  }: GenericTableProps<T>,
  ref: React.Ref<GenericTableHandle<T>>
) {
  const { t } = useAdminTranslation();
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);
  const [page, setPage] = useState(1);
  const [selectedItems, setSelectedItems] = useState<T[]>([]);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [isSearchCountPending, setIsSearchCountPending] = useState(false);
  const searchInputId = useId();

  // Use external sort state if server-side sorting is enabled
  const currentSortBy = serverSideSorting ? externalSortBy : sortBy;
  const currentSortDirection = serverSideSorting ? externalSortDirection : sortDirection;

  const finalNoResultsMessage =
    noResultsMessage || t('studentMetricsDashboard.emptyState.filter.title');

  const getSanitizedSearchQuery = useCallback(
    (query: string) => {
      return query.trim().slice(0, maxSearchQuery);
    },
    [maxSearchQuery]
  );

  const canSubmitSearch = useMemo(() => {
    return searchInputValue.trim().length >= 2;
  }, [searchInputValue]);

  const executeSearch = useCallback(
    (query: string) => {
      const trimmedQuery = query.trim();
      if (query.length > 0 && trimmedQuery.length === 0) {
        return;
      }

      const sanitizedQuery = getSanitizedSearchQuery(query);
      if (sanitizedQuery.length > 0 && sanitizedQuery.length < 2) {
        return;
      }

      if (serverSidePagination && sanitizedQuery !== activeSearchQuery) {
        setIsSearchCountPending(true);
      }
      setSearchInputValue(sanitizedQuery);
      setActiveSearchQuery(sanitizedQuery);
      setPage(1);
      onSearchQueryChange?.(sanitizedQuery);
    },
    [getSanitizedSearchQuery, onSearchQueryChange, serverSidePagination, activeSearchQuery]
  );

  const clearSearch = useCallback(() => {
    setSearchInputValue('');
    setActiveSearchQuery('');
    setIsSearchCountPending(false);
    setPage(1);
    onSearchQueryChange?.('');
  }, [onSearchQueryChange]);

  useEffect(() => {
    if (!showLoader) {
      setIsSearchCountPending(false);
    }
  }, [showLoader]);

  useImperativeHandle(
    ref,
    () => ({
      getSelectedItems: () => {
        return selectedItems;
      },
      setSelectedItems: (items: T[]) => {
        setSelectedItems(items);
      },
      getSearchQuery: () => {
        return activeSearchQuery;
      },
      setSearchQuery: (searchQuery: string) => {
        const sanitizedSearchQuery = getSanitizedSearchQuery(searchQuery);
        setSearchInputValue(sanitizedSearchQuery);
        setActiveSearchQuery(sanitizedSearchQuery);
      },
    }),
    [activeSearchQuery, getSanitizedSearchQuery, selectedItems]
  );

  useEffect(() => {
    onItemsSelect?.(selectedItems);
  }, [onItemsSelect, selectedItems]);

  useEffect(() => {
    onPageChange?.(page);
  }, [onPageChange, page]);

  useEffect(() => {
    setPage(1);
  }, [variant, disablePagination]);

  const handleSort = (column: TableColumn<T>) => {
    if (!column.sortable) return;

    if (serverSideSorting) {
      if (currentSortBy === column.key) {
        const newDirection = currentSortDirection === 'asc' ? 'desc' : 'asc';
        onSortChange?.(column.key, newDirection);
      } else {
        onSortChange?.(column.key, 'asc');
      }
    } else {
      if (sortBy === column.key) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortBy(column.key);
        setSortDirection('asc');
      }
      setPage(1);
    }
  };

  const renderSortIcon = (column: TableColumn<T>) => {
    if (!column.sortable || !showSortArrows) return null;

    return (
      <span className={styles.sortIcon}>
        <AppIcon
          icon='arrowDownActive'
          className={classes(styles.sortArrowActive, {
            [styles.sortArrowActiveOn]:
              currentSortBy === column.key && currentSortDirection === 'asc',
          })}
        />
        <AppIcon
          icon='arrowUpActive'
          className={classes(styles.sortArrowActive, {
            [styles.sortArrowActiveOn]:
              currentSortBy === column.key && currentSortDirection === 'desc',
          })}
        />
      </span>
    );
  };

  const sortedData = useMemo(() => {
    if (serverSideSorting) return data;

    if (!sortBy || !sortDirection) return data;

    return [...data].sort((a, b) => {
      const aValue = (a as any)[sortBy];
      const bValue = (b as any)[sortBy];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.toLowerCase().localeCompare(bValue.toLowerCase())
          : bValue.toLowerCase().localeCompare(aValue.toLowerCase());
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortBy, sortDirection, serverSideSorting]);

  const paginatedData = useMemo(() => {
    if (disablePagination || serverSidePagination) return sortedData;

    const startIndex = (page - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, page, pageSize, disablePagination, variant, serverSidePagination]);

  useEffect(() => {
    onTableViewDataChange?.(paginatedData || []);
  }, [paginatedData, onTableViewDataChange]);

  const onDownloadHandler = useCallback(() => {
    if (serverSideDownload) {
      onDownload?.();
      return;
    }

    const xlsxColumns = columns.filter(col => !!col.xlsxRender);
    const headerRow = xlsxColumns.map(col => col.xlsxLabel || col.label);

    const dataForSheet = sortedData.map(item => {
      const row: any = {};
      xlsxColumns.forEach(col => {
        const formattedValue = col.xlsxRender?.((item as any)[col.key], item);
        row[col.xlsxLabel || col.label] = formattedValue;
      });
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(dataForSheet, {
      header: headerRow,
    });

    const wscols = xlsxColumns.map(col => ({
      wch: (col.xlsxLabel || col.label).length + 5,
    }));
    ws['!cols'] = wscols;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, (downloadFileName || 'Sheet1').slice(0, 31));

    const wbout = XLSX.write(wb, {
      bookType: 'xlsx',
      type: 'array',
    });

    const dataBlob = new Blob([wbout], {
      type: 'application/octet-stream',
      //type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const url = URL.createObjectURL(dataBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${downloadFileName || 'table'}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    onDownload?.();
  }, [columns, sortedData, onDownload, downloadFileName]);

  const showSkeletonLoader = !!showLoader;
  const columnsData = isMultiSelect
    ? [
        {
          key: '--selector',
          label: '',
          width: '40px',
          sortable: false,
          render: (_: any, row: T) => {
            const key = rowKey(row);
            const isSelected = selectedItems.find(i => rowKey(i) === key);
            const isDisabled =
              maxSelectItems && !isSelected && maxSelectItems <= selectedItems.length;

            const onClick = () => {
              if (isDisabled) {
                return;
              }

              if (isSelected) {
                setSelectedItems(prev => {
                  return prev.filter(i => rowKey(i) !== key);
                });
                return;
              }

              setSelectedItems(prev => {
                return [...prev, row];
              });
            };

            return (
              <div
                className={classes(styles.rowSelector, {
                  [styles.rowSelectorSelected]: isSelected,
                  [styles.rowSelectorDisabled]: isDisabled,
                })}
                onClick={onClick}
              ></div>
            );
          },
        } as TableColumn<T>,
        ...columns,
      ]
    : columns;
  const columnsNumber = columnsData.filter(col => !col.hidden).length;

  const gridTemplateColumns = columnsData
    .filter(col => !col.hidden)
    .map(col => col.width || '1fr')
    .join(' ');
  const isSearchActive = showSearch && activeSearchQuery.length > 0;
  const searchResultsCount = serverSidePagination ? totalCount : (sortedData || []).length;
  const searchResultsText =
    isSearchCountPending || showLoader
      ? ''
      : searchDescription ||
        t('genericTable.searchDescription', {
          searchQuery: activeSearchQuery,
          count: searchResultsCount,
        });

  const shouldShowEmptyState =
    paginatedData.length === 0 && !showSkeletonLoader && !showHeaderInEmptyState;

  const emptyStateMessage = (
    <div
      className={styles.emptyState}
      style={
        emptyStateWidth
          ? { width: `${emptyStateWidth}px`, maxWidth: `${emptyStateWidth}px` }
          : undefined
      }
    >
      <div className={styles.emptyStateTitle}>
        {emptyStateTitle
          ? emptyStateTitle.trim()
          : hasData
            ? t('genericTable.noResultsForSearch')
            : finalNoResultsMessage || t('genericTable.noRecords')}
      </div>
      {emptyStateDescription && (
        <div className={styles.emptyStateDesc}>
          {emptyStateDescription}
          {emptyStateSubDescription && (
            <>
              <br />
              {emptyStateSubDescription}
            </>
          )}
        </div>
      )}
      {!hasData && emptyStateAction && (
        <div style={{ marginTop: 16, textDecoration: 'none' }}>{emptyStateAction}</div>
      )}
      {!hasData && !emptyStateAction && !emptyStateDescription && (
        <div className={styles.emptyStateDesc}>
          {t('studentMetricsDashboard.emptyState.filter.description')}
        </div>
      )}
    </div>
  );

  return (
    <div className={`${styles.tableContainer} ${className || ''}`}>
      <div
        className={classes(styles.tableWrapper, {
          [styles.noScroll]: disableScroll,
          [styles.compact]: variant === 'compact',
        })}
      >
        {shouldShowEmptyState ? (
          emptyStateMessage
        ) : (
          <>
            <div
              className={styles.tableWrapperContent}
              style={
                {
                  gridTemplateColumns,
                  '--numberOfColumns': columnsNumber,
                } as any
              }
            >
              <div className={styles.tableStickyHeader}>
                {selectedItems.length === 0 && (title || onDownload || showSearch) && (
                  <>
                    {!isSearchActive && (
                      <div className={styles.titleContainer}>
                        <div className={styles.titleInfo}>
                          {title && (
                            <div className={styles.title}>
                              <div>
                                {title}{' '}
                                {isShowLevelHint && levelHint && (
                                  <span className={styles.levelHint}> | {levelHint}</span>
                                )}
                              </div>
                              {subTitle && <div className={styles.subTitle}>{subTitle}</div>}
                            </div>
                          )}
                        </div>
                        <div className={styles.headerActions}>
                          {showSearch && (
                            <div className={styles.searchContainer}>
                              <button
                                type='button'
                                className={styles.searchLeadingButton}
                                onClick={() => executeSearch(searchInputValue)}
                                disabled={!canSubmitSearch}
                                aria-label={searchPlaceholder}
                              >
                                <AppIcon icon='Search' width={22} height={22} />
                              </button>
                              <input
                                id={searchInputId}
                                name='searchQuery'
                                type='text'
                                value={searchInputValue}
                                maxLength={maxSearchQuery}
                                onChange={e => setSearchInputValue(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key !== 'Enter') {
                                    return;
                                  }

                                  e.preventDefault();
                                  executeSearch(searchInputValue);
                                }}
                                placeholder={searchPlaceholder}
                                className={styles.searchInput}
                              />
                              {/*searchInputValue.length > 0 && (
                                <button
                                  type='button'
                                  className={styles.searchClearButton}
                                  onClick={clearSearch}
                                  aria-label={t('genericTable.clearSelection') || ''}
                                >
                                  <AppIcon icon='x' width={14} height={14} />
                                </button>
                              )*/}
                              <button
                                type='button'
                                className={styles.searchArrowButton}
                                onClick={() => executeSearch(searchInputValue)}
                                disabled={!canSubmitSearch}
                                aria-label={searchPlaceholder}
                              >
                                <AppIcon icon='arrowRight' width={24} height={24} />
                              </button>
                            </div>
                          )}
                          {onDownload && (
                            <button
                              className={styles.iconButton}
                              onClick={onDownloadHandler}
                              aria-label={t('genericTable.downloadAriaLabel') || ''}
                            >
                              <AppIcon icon='downloadIcon' width={24} height={24} />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                    {isSearchActive && (
                      <div className={classes(styles.titleContainer, styles.titleActionsContainer)}>
                        <div className={styles.titleInfo}>
                          <div className={styles.title}>
                            <div>
                              {searchResultsText}{' '}
                              {isShowLevelHint && levelHint && (
                                <span className={styles.levelHint}> | {levelHint}</span>
                              )}
                            </div>
                            {subTitle && <div className={styles.subTitle}>{subTitle}</div>}
                          </div>
                        </div>
                        <div className={styles.selectedActions}>
                          <button type='button' onClick={clearSearch}>
                            {t('genericTable.clearSearch') || 'Clear Search'}
                          </button>
                          {onDownload && (
                            <button
                              className={styles.iconButton}
                              onClick={onDownloadHandler}
                              aria-label={t('genericTable.downloadAriaLabel') || ''}
                            >
                              <AppIcon icon='downloadIcon' width={24} height={24} />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
                {selectedItems.length > 0 && (
                  <div className={classes(styles.titleContainer, styles.titleActionsContainer)}>
                    <div className={styles.title}>
                      <div>
                        {titleRenderOnSelected(selectedItems.length)}{' '}
                        {isShowLevelHint && levelHint && (
                          <span className={styles.levelHint}> | {levelHint}</span>
                        )}
                      </div>
                      {subTitle && <div className={styles.subTitle}>{subTitle}</div>}
                    </div>
                    <div className={styles.selectedActions}>
                      {[
                        ...selectedActions,
                        {
                          text: t('genericTable.clearSelection'),
                          key: 'clear',
                        } as SelectedAction,
                      ]
                        .filter(action => !action.hidden)
                        .map(action => (
                          <button
                            key={`selected-action--${action.key}`}
                            onClick={() => {
                              if (action.key === 'clear') {
                                setSelectedItems([]);
                              }

                              onAction?.(action.key, selectedItems);
                            }}
                          >
                            {typeof action.text === 'function'
                              ? action.text(selectedItems.length)
                              : action.text}
                          </button>
                        ))}
                    </div>
                  </div>
                )}
                {!hideHeaders && (
                  <div className={styles.tableHeader}>
                    {columnsData.map(
                      column =>
                        !column.hidden && (
                          <div
                            key={column.key}
                            className={`${styles.headerCell} ${column.sortable ? styles.sortable : ''}`}
                            onClick={() => handleSort(column)}
                          >
                            <div className={styles.headerContent}>
                              <span>{column.label}</span>
                              {renderSortIcon(column)}
                            </div>
                            {column.filterOptions && column.onFilterChange && (
                              <select
                                className={styles.headerSelect}
                                value={column.filterValue || ''}
                                onChange={e => {
                                  e.stopPropagation();
                                  column.onFilterChange?.(e.target.value);
                                  setPage(1);
                                }}
                                onClick={e => e.stopPropagation()}
                              >
                                {column.filterOptions.map(option => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                        )
                    )}
                  </div>
                )}
              </div>
              <div
                className={classes({
                  [styles.dataContainerNoScroll]: disableScroll,
                  [styles.dataContainer]: !disableScroll,
                  [styles.compactScroll]: variant === 'compact',
                  [styles.dataContainerEmptyState]:
                    paginatedData.length === 0 && !showSkeletonLoader,
                })}
                style={{
                  ...(variant === 'compact'
                    ? {
                        overflow: 'visible',
                        style,
                      }
                    : {}),
                }}
              >
                {showSkeletonLoader
                  ? new Array(Math.min(pageSize, 10)).fill(0).map((_, i) => {
                      return (
                        <div key={`table-skelton-loader--${i}`} className={styles.tableRow}>
                          {columnsData.map(column => {
                            if (column.hidden) {
                              return null;
                            }

                            return (
                              <div
                                key={`table-skelton-loader--${i}--${column.key}`}
                                className={styles.cell}
                              >
                                {column.label && <SkeltonLoader />}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })
                  : paginatedData.length === 0
                    ? emptyStateMessage
                    : paginatedData.map(row => {
                        const RowComponent = detailsPath ? Link : 'div';
                        const rowProps = detailsPath
                          ? {
                              to: detailsPath(row),
                              className: styles.tableRow,
                              style: {
                                cursor: 'pointer',
                                textDecoration: 'none',
                                color: 'inherit',
                              },
                            }
                          : {
                              className: styles.tableRow,
                              onClick: onRowClick ? () => onRowClick(row) : undefined,
                            };

                        return (
                          <RowComponent key={rowKey(row)} {...(rowProps as any)}>
                            {columnsData.map(column => {
                              if (column.hidden) {
                                return null;
                              }

                              const rawValue = (row as any)[column.key];
                              const isLoginColumn =
                                column.key === 'firstLogin' || column.key === 'lastLogin';
                              const isStringCell = typeof rawValue === 'string' && !isLoginColumn;
                              return (
                                <div
                                  key={column.key}
                                  className={`${styles.cell} ${isStringCell ? styles.alignLeft : ''}`}
                                >
                                  {column.render ? column.render(rawValue, row) : rawValue}
                                </div>
                              );
                            })}
                          </RowComponent>
                        );
                      })}
              </div>
            </div>
          </>
        )}
      </div>
      {!serverSidePagination &&
        !disablePagination &&
        sortedData.length > pageSize &&
        paginatedData.length > 0 && (
          <Pagination
            currentPage={page}
            totalPages={Math.ceil(sortedData.length / pageSize)}
            onPageChange={setPage}
          />
        )}
      {serverSidePagination && !disablePagination && totalPages > 1 && (
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setPage} />
      )}
    </div>
  );
}

export default forwardRef(GenericTable) as <T>(
  props: GenericTableProps<T> & { ref?: React.Ref<GenericTableHandle<T>> }
) => React.ReactElement;
